import { useEffect, useState } from "react";
import { Send, Plus, Trash2, CheckCircle2, XCircle, Power, Pencil, X } from "lucide-react";
import { http, fmtDateTime, errMsg } from "../api";

const EMPTY = { label: "", bot_token: "", chat_id: "", events: [], active: true };

function TargetForm({ initial, events, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial.id;
  const toggle = k => setForm(f => ({ ...f, events: f.events.includes(k) ? f.events.filter(e => e !== k) : [...f.events, k] }));
  const save = async () => {
    setBusy(true); setNotice("");
    try {
      const payload = { label: form.label, chat_id: form.chat_id, events: form.events, active: form.active };
      if (form.bot_token) payload.bot_token = form.bot_token;
      if (isEdit) await http.patch(`/admin/telegram/${initial.id}`, payload);
      else await http.post("/admin/telegram", payload);
      onSaved();
    } catch (e) { setNotice(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="telegram-form">
        <div className="modal-head"><h2>{isEdit ? "Edit recipient" : "Add Telegram recipient"}</h2><button className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="form-notice">
          <strong>How to get these:</strong> open <b>@BotFather</b> in Telegram → <code>/newbot</code> → copy the token. Send any message to your bot (or add it to a group), then open
          <code> https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> and copy <code>chat.id</code>. One bot can serve many people — just add each person's chat id as a separate recipient.
        </div>
        <div className="field-row">
          <div><label>Label (who gets this)</label><input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. Owner phone, Ops team group" data-testid="tg-label" /></div>
          <div><label>Chat ID</label><input value={form.chat_id} onChange={e => setForm({ ...form, chat_id: e.target.value })} placeholder="123456789 or -100…" data-testid="tg-chat-id" /></div>
        </div>
        <div style={{ marginTop: 10 }}>
          <label>Bot token {isEdit && <span style={{ color: "var(--muted)", fontWeight: 400 }}>(leave blank to keep existing: {initial.bot_token_masked})</span>}</label>
          <input value={form.bot_token} onChange={e => setForm({ ...form, bot_token: e.target.value })} placeholder="123456789:AAH…" data-testid="tg-token" />
        </div>
        <div style={{ marginTop: 12 }}>
          <label>Send alerts for</label>
          <div className="chip-row" style={{ marginBottom: 6 }}>
            <button type="button" className="chip" onClick={() => setForm({ ...form, events: events.map(e => e.key) })}>Select all</button>
            <button type="button" className="chip" onClick={() => setForm({ ...form, events: [] })}>Clear</button>
          </div>
          <div className="event-grid">
            {events.map(e => (
              <label key={e.key} className={`event-opt ${form.events.includes(e.key) ? "on" : ""}`} data-testid={`tg-event-${e.key}`}>
                <input type="checkbox" style={{ width: "auto" }} checked={form.events.includes(e.key)} onChange={() => toggle(e.key)} /> {e.label}
              </label>
            ))}
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, cursor: "pointer" }}>
          <input type="checkbox" style={{ width: "auto" }} checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="tg-active" /> Active
        </label>
        {notice && <div className="notice error" style={{ marginTop: 12 }}>{notice}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button className="primary" onClick={save} disabled={busy || !form.label || !form.chat_id || (!isEdit && !form.bot_token)} data-testid="tg-save">{busy ? "Saving…" : isEdit ? "Save changes" : "Add recipient"}</button>
          <button className="ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [targets, setTargets] = useState([]);
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const load = () => http.get("/admin/telegram").then(r => setTargets(r.data));
  useEffect(() => { load(); http.get("/admin/telegram/events").then(r => setEvents(r.data)); }, []);
  const labelOf = k => events.find(e => e.key === k)?.label || k;

  const test = async t => {
    setNotice(null);
    try { await http.post(`/admin/telegram/${t.id}/test`); setNotice({ tone: "success", text: `Test message sent to ${t.label}` }); load(); }
    catch (e) { setNotice({ tone: "error", text: errMsg(e) }); load(); }
  };
  const toggleActive = async t => { await http.patch(`/admin/telegram/${t.id}`, { active: !t.active }); load(); };
  const remove = async t => {
    if (!window.confirm(`Remove "${t.label}"? They will stop receiving alerts.`)) return;
    await http.delete(`/admin/telegram/${t.id}`); load();
  };

  return (
    <>
      <div className="page-head">
        <div><h1>Telegram alerts</h1><p>Connect one or many bots and people. Each recipient chooses which events they get.</p></div>
        <button className="primary" onClick={() => setEditing(EMPTY)} data-testid="tg-add"><Plus size={14} /> Add recipient</button>
      </div>
      {notice && <div className={`notice ${notice.tone}`} data-testid="tg-notice">{notice.text}</div>}
      <div className="list-rows">
        {targets.map(t => (
          <div key={t.id} className={`list-row ${t.last_error ? "danger-border" : ""}`} data-testid={`tg-target-${t.id}`}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: t.active ? "var(--primary-soft)" : "var(--surface-2)", color: t.active ? "var(--primary)" : "var(--muted)", display: "grid", placeItems: "center", flexShrink: 0 }}><Send size={18} /></div>
            <div className="list-row-info">
              <h3>{t.label} <span style={{ color: "var(--muted)", fontWeight: 500, fontSize: 12.5 }}>· chat {t.chat_id} · bot {t.bot_token_masked}</span></h3>
              <div className="tag-row" style={{ marginTop: 6 }}>{t.events.map(k => <span className="badge" key={k}>{labelOf(k)}</span>)}{t.events.length === 0 && <span className="badge warning">No events selected</span>}</div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 6 }}>
                {t.sent_count || 0} sent · last {t.last_sent_at ? fmtDateTime(t.last_sent_at) : "never"}
                {t.last_error && <span style={{ color: "var(--danger)", marginLeft: 8 }}><XCircle size={11} style={{ display: "inline" }} /> {t.last_error}</span>}
                {!t.last_error && t.last_sent_at && <span style={{ color: "var(--success)", marginLeft: 8 }}><CheckCircle2 size={11} style={{ display: "inline" }} /> OK</span>}
              </div>
            </div>
            <div className="list-row-actions">
              <span className={`badge ${t.active ? "success" : ""}`}>{t.active ? "Active" : "Paused"}</span>
              <button className="pill-action neutral" onClick={() => test(t)} data-testid={`tg-test-${t.id}`}><Send size={13} /> Test</button>
              <button className="pill-action neutral" onClick={() => setEditing({ ...t, bot_token: "" })} data-testid={`tg-edit-${t.id}`}><Pencil size={13} /></button>
              <button className="pill-action neutral" onClick={() => toggleActive(t)} title={t.active ? "Pause" : "Resume"} data-testid={`tg-toggle-${t.id}`}><Power size={13} /></button>
              <button className="pill-action reject" onClick={() => remove(t)} data-testid={`tg-delete-${t.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {targets.length === 0 && (
          <div className="empty"><Send size={30} /><div style={{ fontWeight: 600, marginTop: 8 }}>No Telegram recipients yet</div><p style={{ fontSize: 13, marginTop: 4 }}>Add your bot token and chat id to get instant alerts for every hire request and acknowledgement.</p></div>
        )}
      </div>
      {editing && <TargetForm initial={editing} events={events} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}
